import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { NbGlobalLogicalPosition, NbToastrService } from '@nebular/theme';
import { Subscription, forkJoin, of, Observable } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { ChatSignalRService } from '../../../pages/negocio/cliente/components/chat-pergunta-resposta/chat-signalr.service';
import { ChatPerguntaRespostaService } from '../../../pages/negocio/cliente/components/chat-pergunta-resposta/chat-pergunta-resposta.service';
import { NegocioDataService } from '../../../pages/negocio/shared/services/NegocioController/negocio-data.services';
import { ehPerfilGerencia, TipoUsuario, obterDescricaoTipoUsuario } from '../../../shared/Enums/TipoUsuario';
import { ListaCliente } from '../../../shared/Operations/listaCliente';
import { RegistrarAcessoService } from '../../../shared/services/registrar-acesso';

interface AvisoChat {
  id: string;
  clienteId: string;
  nomeCliente: string;
  remetenteNome: string;
  tipoUsuarioEnvio: number;
  executivoId: string;
  paraTimeOperacional: boolean;
  dataEnvio: string;
  lida: boolean;
  aberta: boolean;
}

@Component({
  selector: 'ngx-chat-executivo-operacional',
  templateUrl: './chat-executivo-operacional.component.html',
  styleUrls: ['./chat-executivo-operacional.component.scss']
})
export class ChatExecutivoOperacionalComponent implements OnInit, OnDestroy {
  visivel = false;
  mostrarLista = false;
  atualizando = false;
  avisos: AvisoChat[] = [];
  clienteLinkCarregandoId: string | null = null;

  private readonly logicalPositions = NbGlobalLogicalPosition;
  private assinatura?: Subscription;
  private assinaturaVista?: Subscription;
  private meuId = '';
  private comoExecutivo = false;
  private comoOperacional = false;

  constructor(
    private chatHub: ChatSignalRService,
    private api: ChatPerguntaRespostaService,
    private negocioDataService: NegocioDataService,
    private registrarAcessoService: RegistrarAcessoService,
    private toastrService: NbToastrService,
    private router: Router
  ) {}

  ngOnInit(): void {
    const tipo = sessionStorage.getItem('tipoUsuario') || '';
    this.visivel = this.podeVer(tipo);
    if (!this.visivel) {
      return;
    }

    this.meuId = (sessionStorage.getItem('usuarioId') || '').toLowerCase();
    this.comoExecutivo = tipo === TipoUsuario.Executivo
      || tipo === TipoUsuario.GerenteExecutivo
      || ehPerfilGerencia(tipo);
    this.comoOperacional = tipo === TipoUsuario.Operacional
      || tipo === TipoUsuario.GerenteOperacional
      || ehPerfilGerencia(tipo);
    this.assinatura = this.chatHub.notificacoes$.subscribe(evento => this.receber(evento));
    this.assinaturaVista = this.api.notificacaoVista$.subscribe(id => this.marcarLocal(id));
    this.carregar();
    void this.entrar();
  }

  ngOnDestroy(): void {
    this.assinatura?.unsubscribe();
    this.assinaturaVista?.unsubscribe();
    if (this.visivel) {
      void this.chatHub.liberar();
    }
  }

  get naoLidos(): number {
    return this.avisos.filter(aviso => !aviso.lida).length;
  }

  toggleLista(): void {
    this.mostrarLista = !this.mostrarLista;
  }

  marcarAoClicar(aviso: AvisoChat): void {
    if (!aviso.lida) {
      this.marcarAviso(aviso);
    }
  }

  atualizar(event: Event): void {
    event.stopPropagation();
    this.atualizando = true;
    this.carregar();
  }

  descricaoTipo(tipo: number): string {
    return obterDescricaoTipoUsuario(tipo);
  }

  abrirNestaAba(event: Event, aviso: AvisoChat): void {
    event.preventDefault();
    event.stopPropagation();
    this.abrirCliente(aviso, false);
  }

  abrirNovaAba(event: Event, aviso: AvisoChat): void {
    event.preventDefault();
    event.stopPropagation();
    this.abrirCliente(aviso, true);
  }

  fecharAviso(event: Event, aviso: AvisoChat): void {
    event.preventDefault();
    event.stopPropagation();
    if (!aviso.id) {
      return;
    }
    this.api.ocultarNotificacao(aviso.id);
    this.avisos = this.avisos.filter(item => item.id !== aviso.id);
  }

  private podeVer(tipo: string): boolean {
    return tipo === TipoUsuario.Executivo
      || tipo === TipoUsuario.GerenteExecutivo
      || tipo === TipoUsuario.Operacional
      || tipo === TipoUsuario.GerenteOperacional
      || ehPerfilGerencia(tipo);
  }

  private async entrar(): Promise<void> {
    await this.chatHub.usar();
    if (this.comoExecutivo) {
      await this.chatHub.entrarInboxExecutivo(this.meuId);
    }
    if (this.comoOperacional) {
      await this.chatHub.entrarInboxOperacional();
    }
  }

  private carregar(): void {
    const chamadas: Observable<AvisoChat[]>[] = [];
    if (this.comoExecutivo) {
      chamadas.push(this.api.listarNotificacoesExecutivo().pipe(
        map(resposta => this.mapearLista(resposta, false)),
        catchError(() => of([] as AvisoChat[]))
      ));
    }
    if (this.comoOperacional) {
      chamadas.push(this.api.listarNotificacoesOperacional().pipe(
        map(resposta => this.mapearLista(resposta, true)),
        catchError(() => of([] as AvisoChat[]))
      ));
    }
    if (!chamadas.length) {
      this.atualizando = false;
      return;
    }

    forkJoin(chamadas).subscribe({
      next: listas => {
        const vindas = listas
          .reduce((todas, lista) => todas.concat(lista), [] as AvisoChat[])
          .filter(aviso => !this.api.notificacaoOculta(aviso.id));
        const ids = new Set(vindas.map(aviso => aviso.id));
        const doHub = this.avisos.filter(aviso => aviso.id && !ids.has(aviso.id) && !this.api.notificacaoOculta(aviso.id));
        this.avisos = [...doHub, ...vindas].slice(0, 40);
        this.atualizando = false;
      },
      error: () => {
        this.atualizando = false;
      }
    });
  }

  private mapearLista(resposta: any, paraTimeOperacional: boolean): AvisoChat[] {
    const dados = resposta?.data ?? resposta?.Data;
    const lista = Array.isArray(dados) ? dados : [];
    return lista.map((item: any) => this.mapearItem(item, paraTimeOperacional)).filter(aviso => !!aviso.id);
  }

  private mapearItem(item: any, paraTimeOperacional: boolean): AvisoChat {
    return {
      id: String(item?.Id ?? item?.id ?? ''),
      clienteId: String(item?.ClienteId ?? item?.clienteId ?? ''),
      nomeCliente: String(item?.NomeCliente ?? item?.nomeCliente ?? 'Cliente'),
      remetenteNome: String(item?.RemetenteNome ?? item?.remetenteNome ?? 'Usuário'),
      tipoUsuarioEnvio: Number(item?.TipoUsuarioEnvio ?? item?.tipoUsuarioEnvio ?? 0),
      executivoId: String(item?.ExecutivoId ?? item?.executivoId ?? '').toLowerCase(),
      paraTimeOperacional,
      dataEnvio: String(item?.DataEnvio ?? item?.dataEnvio ?? ''),
      lida: false,
      aberta: false
    };
  }

  private marcarLocal(id: string): void {
    const aviso = this.avisos.find(item => item.id.toLowerCase() === id.toLowerCase());
    if (aviso) {
      aviso.lida = true;
      this.avisos = [...this.avisos];
    }
  }

  private marcarAviso(aviso: AvisoChat): void {
    const chamada = aviso.paraTimeOperacional
      ? this.api.marcarNotificacaoOperacional(aviso.id)
      : this.api.marcarNotificacaoExecutivo(aviso.id);
    chamada.subscribe({
      next: () => {
        aviso.lida = true;
      },
      error: () => undefined
    });
  }

  private receber(evento: Record<string, unknown>): void {
    const id = this.texto(evento, 'Id');
    if (!id || id === '00000000-0000-0000-0000-000000000000') {
      return;
    }
    const remetenteId = this.texto(evento, 'RemetenteEmployeeId').toLowerCase();
    if (remetenteId && remetenteId === this.meuId) {
      return;
    }
    if (this.avisos.some(aviso => aviso.id && aviso.id === id)) {
      return;
    }
    if (this.api.notificacaoOculta(id)) {
      return;
    }

    const aviso: AvisoChat = {
      id,
      clienteId: this.texto(evento, 'ClienteId'),
      nomeCliente: this.texto(evento, 'NomeCliente') || 'Cliente',
      remetenteNome: this.texto(evento, 'RemetenteNome') || 'Usuário',
      tipoUsuarioEnvio: Number(evento['TipoUsuarioEnvio'] ?? evento['tipoUsuarioEnvio'] ?? 0),
      executivoId: this.texto(evento, 'ExecutivoId').toLowerCase(),
      paraTimeOperacional: evento['ParaTimeOperacional'] === true || evento['paraTimeOperacional'] === true,
      dataEnvio: this.texto(evento, 'DataEnvio'),
      lida: this.api.notificacaoJaVista(id),
      aberta: false
    };

    if (!aviso.paraTimeOperacional && aviso.executivoId && aviso.executivoId !== this.meuId) {
      return;
    }

    this.avisos = [aviso, ...this.avisos].slice(0, 40);
  }

  private abrirCliente(aviso: AvisoChat, novaAba: boolean): void {
    if (!aviso.clienteId || this.clienteLinkCarregandoId) {
      return;
    }

    const destino = novaAba ? window.open('', '_blank') : null;
    if (novaAba && !destino) {
      this.toastrService.warning('Permita pop-ups para abrir o cliente em nova aba.', 'Atenção', {
        position: this.logicalPositions.TOP_END
      });
      return;
    }

    this.clienteLinkCarregandoId = aviso.id;
    this.buscarNegocio(aviso.clienteId).subscribe(negocio => {
      this.clienteLinkCarregandoId = null;
      if (!negocio) {
        destino?.close();
        this.toastrService.warning('Não retornou dados', 'Atenção', { position: this.logicalPositions.TOP_END });
        return;
      }

      const cliente = this.mapearClienteNegocio(negocio);
      const url = `${environment.siteUrl}/pages/negocio/cliente/editar`;
      if (destino) {
        this.gravarCliente(destino, cliente);
        this.registrarAcessoService.registerUserIP(cliente);
        destino.location.href = `${url}?aba=1`;
        return;
      }

      this.gravarCliente(window, cliente);
      this.registrarAcessoService.registerUserIP(cliente);
      this.mostrarLista = false;
      this.router.navigate(['/pages/negocio/cliente/editar']);
    });
  }

  private buscarNegocio(clienteId: string) {
    return this.negocioDataService.CarregarNegociosCliente(clienteId).pipe(
      switchMap(value => {
        const negocios = value?.data ?? value?.Data;
        const success = value?.success ?? value?.Success;
        if (success && Array.isArray(negocios) && negocios.length > 0) {
          return of(negocios[0]);
        }
        return this.negocioDataService.GetPrimeiroNegocioDoClient(clienteId).pipe(
          map(result => this.negocioDe(result))
        );
      }),
      catchError(() => this.negocioDataService.GetPrimeiroNegocioDoClient(clienteId).pipe(
        map(result => this.negocioDe(result)),
        catchError(() => of(null))
      ))
    );
  }

  private negocioDe(result: any): any {
    const negocio = result?.data ?? result?.Data;
    const ok = result?.success ?? result?.Success;
    return ok && negocio ? negocio : null;
  }

  private gravarCliente(destino: Window, cliente: ListaCliente): void {
    const payload = JSON.stringify(cliente);
    try {
      destino.sessionStorage.setItem('cliente', payload);
    } catch {
      sessionStorage.setItem('cliente', payload);
    }
  }

  private mapearClienteNegocio(negocio: any): ListaCliente {
    return {
      idNegocio: negocio.id,
      idCliente: negocio.Client.id,
      nomeCliente: negocio.Client.name,
      cpfCnpj: negocio.Client.document_Number,
      analista: negocio.Employee.employeeName,
      dadosUsuario: negocio.Employee,
      dataNascimento: negocio.Client.birthDate != null ? negocio.Client.birthDate : null,
      profissao: negocio.Client.profession != null ? negocio.Client.profession : '',
      endereco: negocio.Client.address != null ? negocio.Client.address : '',
      cidade: negocio.Client.city != null ? negocio.Client.city : '',
      estado: negocio.Client.state != null ? negocio.Client.state : '',
      dataObito: negocio.Client.deathDate != null ? negocio.Client.deathDate : null,
      cadastro: negocio.company != null ? negocio.company : '',
      situacaoCpfCnpj: negocio.Client.ClientSituation != null && negocio.Client.ClientSituation.code != null ? negocio.Client.ClientSituation.code.toString() : null,
      dadoObito: negocio.Client.deathData != null ? negocio.Client.deathData : '',
      negocio,
      situacaoNegocio: '',
      analistaFeedback: '',
      dtContato: null,
      dtRetorno: null,
      retornarEm: null,
      obito: negocio.Client.obito,
      feedback: null,
      analistaCompartilhado: negocio.AnalistaCompartilhado,
      valorAtual: null,
      dtOutroRetorno: null,
      dataNegocioFechado: null,
      idAnalista: negocio.Employee.Id,
      porcentagemReno: negocio.closedPercentage != null ? negocio.closedPercentage : null,
      valorPrevisaoReno: negocio.valorPrevisaoReno != null ? negocio.valorPrevisaoReno : null,
      temHerdeiro: negocio.TemHerdeiro,
      tipoConhecimentoAtivo: negocio.TipoConhecimentoAtivos != null ? negocio.TipoConhecimentoAtivos.codigo : null,
      valorSaldo: negocio.ValorSaldo != null ? negocio.ValorSaldo : null,
      valorAtualNegocio: (negocio.valorAtual != null ? negocio.valorAtual : 0) + (negocio.ValorSaldo != null ? negocio.ValorSaldo : 0),
      temHerdeiroComPagamento: negocio.PagamentoEfetuado != null ? negocio.PagamentoEfetuado : false,
      faseNegocio: negocio.FaseNegocio != null ? negocio.FaseNegocio : null,
      previsaoCaixa: null
    };
  }

  private texto(origem: Record<string, unknown>, nome: string): string {
    const camel = nome.charAt(0).toLowerCase() + nome.slice(1);
    const valor = origem[nome] ?? origem[camel];
    return valor == null ? '' : String(valor);
  }
}
